/*
 * <fade-slideshow> - a crossfade variant of Dawn's SlideshowComponent.
 *
 * Desktop (>=750px): slides are stacked (see component-slideshow.css) and
 * navigation switches the active slide via a class, so the background image
 * crossfades and the message area fades + moves vertically. No horizontal
 * scroll is used.
 *
 * Mobile (<750px): behaviour is delegated to Dawn's scroll/swipe slideshow
 * unchanged, so the fade only happens on the non-mobile version.
 *
 * It also replaces setAutoPlay / focus handling: with the pause button removed
 * the inherited versions reference announcement-bar-only state and would throw.
 */
if (typeof SlideshowComponent !== 'undefined' && !customElements.get('fade-slideshow')) {
  const FADE_MS = 600; // keep in sync with component-slideshow.css

  class FadeSlideshowComponent extends SlideshowComponent {
    constructor() {
      super();
      if (!this.slider || !this.sliderItemsToShow || this.sliderItemsToShow.length === 0) return;

      this.desktopQuery = window.matchMedia('(min-width: 750px)');
      this.currentPage = this.currentPage || 1;

      if (this.pageTotalElement) this.pageTotalElement.textContent = this.sliderItemsToShow.length;
      this.applyActiveClasses(this.currentPage);
      this.updateControlButtons();

      // Re-assert the active slide when crossing the mobile/desktop boundary.
      this.desktopQuery.addEventListener('change', () => this.applyActiveClasses(this.currentPage));
    }

    // Evaluated lazily so it is safe to call during the base constructor.
    get isFade() {
      this.desktopQuery = this.desktopQuery || window.matchMedia('(min-width: 750px)');
      return this.desktopQuery.matches;
    }

    // In fade mode the slider does not scroll, so the scroll-driven page
    // calculation must be skipped; mobile keeps Dawn's behaviour.
    update() {
      if (this.isFade) return;
      super.update();
    }

    onButtonClick(event) {
      if (!this.isFade) return super.onButtonClick(event);
      event.preventDefault();
      const step = parseInt(event.currentTarget.dataset.step || '1', 10);
      const forward = event.currentTarget.name === 'next';
      this.select(this.currentPage + (forward ? step : -step), forward ? 'next' : 'previous');
    }

    linkToSlide(event) {
      if (!this.isFade) return super.linkToSlide(event);
      event.preventDefault();
      const target = this.sliderControlLinksArray.indexOf(event.currentTarget) + 1;
      this.select(target, target < this.currentPage ? 'previous' : 'next');
    }

    // Advance one slide: crossfade on desktop, native scroll on mobile.
    advance() {
      if (this.isFade) return this.select(this.currentPage + 1, 'next');
      super.autoRotateSlides();
    }

    // direction: 'next' (default) moves the text upward, 'previous' reverses it.
    select(page, direction = 'next') {
      const count = this.sliderItemsToShow.length;
      if (count < 2) return;
      const next = (((page - 1) % count) + count) % count + 1; // wrap into 1..count
      if (next === this.currentPage) return;

      const previous = this.currentPage;
      this.currentPage = next;

      this.applyActiveClasses(next, previous, direction);
      this.updateControlButtons();
      if (this.currentPageElement) this.currentPageElement.textContent = next;
      this.setSlideVisibility();

      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: { currentPage: next, currentElement: this.sliderItemsToShow[next - 1] },
        })
      );
    }

    applyActiveClasses(page, previous, direction = 'next') {
      const reverse = direction === 'previous';

      this.sliderItemsToShow.forEach((slide, index) => {
        const active = index === page - 1;
        slide.classList.toggle('slideshow__slide--active', active);
        if (active) slide.classList.toggle('slideshow__slide--reverse', reverse);
      });

      if (previous && previous !== page) {
        const leaving = this.sliderItemsToShow[previous - 1];
        if (leaving) {
          leaving.classList.toggle('slideshow__slide--reverse', reverse);
          leaving.classList.add('slideshow__slide--leaving');
          setTimeout(() => {
            leaving.classList.remove('slideshow__slide--leaving');
            leaving.classList.remove('slideshow__slide--reverse');
          }, FADE_MS);
        }
      }
    }

    updateControlButtons() {
      this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
      if (!this.sliderControlButtons.length) return;
      this.sliderControlButtons.forEach((link) => {
        link.classList.remove('slider-counter__link--active');
        link.removeAttribute('aria-current');
      });
      const active = this.sliderControlButtons[this.currentPage - 1];
      if (active) {
        active.classList.add('slider-counter__link--active');
        active.setAttribute('aria-current', true);
      }
    }

    // Self-contained autoplay. The inherited setAutoPlay / play / pause assume
    // an autoplay button and announcement-bar-only state that no longer exist,
    // so we own the timer here and advance via the same select() path as manual
    // navigation. Called by the base constructor when data-autoplay is "true".
    setAutoPlay() {
      this.reducedMotion = this.reducedMotion || window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplaySpeed = (parseFloat(this.slider.dataset.speed) || 5) * 1000;
      this.autoplayActive = !this.reducedMotion.matches;
      if (!this.autoplayActive) return;

      // Pause while the visitor is interacting with the slideshow.
      this.addEventListener('mouseenter', this.pause.bind(this));
      this.addEventListener('mouseleave', this.play.bind(this));
      this.addEventListener('focusin', this.pause.bind(this));
      this.addEventListener('focusout', this.play.bind(this));

      this.play();
    }

    play() {
      if (!this.autoplayActive) return;
      this.slider.setAttribute('aria-live', 'off');
      clearInterval(this.autoplay);
      this.autoplay = setInterval(() => this.advance(), this.autoplaySpeed);
    }

    pause() {
      this.slider.setAttribute('aria-live', 'polite');
      clearInterval(this.autoplay);
    }
  }

  customElements.define('fade-slideshow', FadeSlideshowComponent);
}
